import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { uniq, shuffle } from 'underscore';
import Slider from 'react-slick';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import configData from '../../signals/configData';
import translation from '../../signals/translations';
import useData from '../../hooks/useData';
import IconClose from '../../icons/IconClose';
import IconHeart from '../../icons/IconHeart';
import IconHeartFilled from '../../icons/IconHeartFilled';
import IconMarkerSearch from '../../icons/IconMarkerSearch';
import IconInstagram from '../../icons/IconInstagram';
import IconFacebook from '../../icons/IconFacebook';
import IconLINE from '../../icons/IconLINE';
import useRecord from '../../hooks/useRecord';
import textareaToHTML from '../../tools/textareaToHTML';
import useRecommend from '../../hooks/useRecommend';

const Prev = (props) => {
	return <div className='nav prev' onClick={props.onClick} />;
};

const Next = (props) => {
	return <div className='nav next' onClick={props.onClick} />;
};

const ExhibitorInfo = () => {
	const { exhibitorInfo } = jsonData.value;
	const { ExhibitorInfo, selectedProduct, selectedExhibitor } = appState.value;
	const { recommendFromAPI, showRightContent } = configData.value;
	const { toggleFavourite } = useData();
	const [product, setProduct] = useState(null);
	// 使用 useRef 以避免 callback ref 在 mount/unmount 間切換為 null 造成競態
	const sliderRef = useRef(null);
	const { makeRecord } = useRecord();
	const { fetchRecommend, relatedExhibitorsFromAPI } = useRecommend();

	// 直接使用 sliderRef 掛載在 Slider 上

	useEffect(() => {
		if (selectedProduct) {
			setProduct(selectedProduct);
		}
	}, [selectedProduct]);

	const exhibitor = useMemo(() => {
		return exhibitorInfo.find((exhibitor) => exhibitor['攤位號碼'] === selectedExhibitor);
	}, [exhibitorInfo, selectedExhibitor]);

	// useEffect(() => {
	// 	if (exhibitor) {
	// 		exhibitor.products = [...exhibitor.products, ...exhibitor.products, ...exhibitor.products];
	// 	}
	// }, [exhibitor]);

	const closePopup = useCallback(() => {
		appState.value = {
			...appState.value,
			ExhibitorInfo: false,
		};

		setTimeout(() => {
			appState.value = {
				...appState.value,
				selectedExhibitor: null,
				selectedProduct: null,
			};

			setProduct(null);
		}, 500);
	}, [setProduct]);

	const toggleFavouriteExhibitor = useCallback(() => {
		toggleFavourite(selectedExhibitor);
	}, [selectedExhibitor, toggleFavourite]);

	const categories = useMemo(() => {
		const allCategories = [];

		exhibitor?.products.forEach((product) => {
			if (product['產品小分類']) {
				allCategories.push(product['產品小分類']);
				return;
			}

			if (product['產品中分類']) {
				allCategories.push(product['產品中分類']);
				return;
			}

			if (product['產品大分類']) {
				allCategories.push(product['產品大分類']);
				return;
			}
		});

		const uniqueCategories = uniq(allCategories);

		return uniqueCategories;
	}, [exhibitor]);

	const categoryLevel = useMemo(() => {
		const firstProduct = exhibitor?.products[0];
		if (!firstProduct) return null;
		if (firstProduct['產品小分類']) return '產品小分類';
		if (firstProduct['產品中分類']) return '產品中分類';
		if (firstProduct['產品大分類']) return '產品大分類';
		return null;
	}, [exhibitor]);

	const relatedExhibitors = useMemo(() => {
		const relatedExhibitors = [];
		const otherExhibitors = exhibitorInfo.filter((exhibitor) => exhibitor['攤位號碼'] !== selectedExhibitor);

		otherExhibitors.forEach((exhibitor) => {
			const newExhibitor = { ...exhibitor };

			if (categoryLevel === '產品小分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					return categories.includes(product['產品小分類']);
				});
			}

			if (categoryLevel === '產品中分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					return categories.includes(product['產品中分類']);
				});
			}

			if (categoryLevel === '產品大分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					return categories.includes(product['產品大分類']);
				});
			}

			if (newExhibitor.products.length > 0) {
				relatedExhibitors.push(newExhibitor);
			}
		});

		return shuffle(relatedExhibitors);
	}, [exhibitorInfo, selectedExhibitor, categoryLevel, categories]);

	useEffect(() => {
		if (exhibitor && exhibitor['攤位號碼']) {
			fetchRecommend(exhibitor['攤位號碼']);
		}
	}, [exhibitor, fetchRecommend]);

	const recordExhibitorClicked = useCallback(
		(booth) => {
			makeRecord({
				Action: 'T',
				Booth: booth,
			});
		},
		[makeRecord]
	);

	const recordProductClicked = useCallback(
		(product, booth) => {
			makeRecord({
				Action: 'T',
				Booth: booth || selectedExhibitor,
				Product: product['產品名稱'],
			});
		},
		[makeRecord, selectedExhibitor]
	);

	const showProduct = useCallback(
		(product) => {
			recordProductClicked(product);
			setProduct(product);
		},
		[recordProductClicked]
	);

	const chooseExhibitor = (exhibitor) => {
		appState.value = {
			...appState.value,
			activeFloor: Number(exhibitor.floor),
			selectedBooth: exhibitor['攤位號碼'],
			selectedExhibitor: exhibitor['攤位號碼'],
		};

		setProduct(null);
	};

	const chooseProduct = useCallback(
		(relatedExhibitor, index) => {
			appState.value = {
				...appState.value,
				selectedExhibitor: relatedExhibitor['攤位號碼'],
				activeFloor: Number(relatedExhibitor.floor),
				selectedBooth: relatedExhibitor['攤位號碼'],
			};

			const product = relatedExhibitor.products[index];

			setProduct(product);
			recordProductClicked(product, relatedExhibitor['攤位號碼']);
		},
		[recordProductClicked]
	);

	useEffect(() => {
		// 僅在有 slick 實例時才做跳轉，避免 null 讀取
		const instance = sliderRef.current;
		if (exhibitor && product && instance && typeof instance.slickGoTo === 'function') {
			const exhibitorProductIndex = exhibitor.products.findIndex((p) => p['產品名稱'] === product['產品名稱']);
			if (exhibitorProductIndex >= 0) {
				instance.slickGoTo(exhibitorProductIndex);
			}
		}
	}, [exhibitor, product]);

	const close = useCallback(
		(event) => {
			if (event.target === event.currentTarget) {
				appState.value = {
					...appState.value,
					ExhibitorInfo: false,
				};

				setTimeout(() => {
					appState.value = {
						...appState.value,
						selectedExhibitor: null,
						selectedProduct: null,
					};

					setProduct(null);
				}, 500);
			}
		},
		[setProduct]
	);

	// 是否使用 slick：產品數 > 3 才啟用；否則顯示靜態卡片
	const productCount = exhibitor?.products.length || 0;
	const useSlider = productCount > 3;

	const slidesToShow = useMemo(() => {
		if (window.innerWidth <= 768) {
			// 行動版維持原本：>1 顯示 2，否則 1
			return productCount > 1 ? 2 : 1;
		}
		// 桌機版使用 slick 時固定顯示 3；不使用 slick 時不影響（靜態）
		return 3;
	}, [productCount]);

	const scrollerRef = useRef(null);

	const navigateToBooth = useCallback(() => {
		makeRecord({
			Action: 'N',
			Booth: selectedExhibitor,
		});

		appState.value = {
			...appState.value,
			ExhibitorInfo: false,
			selectedBooth: selectedExhibitor,
			navigation: true,
		};
	}, [makeRecord, selectedExhibitor]);

	const city = useMemo(() => {
		if (exhibitor) {
			return exhibitor['所在城市'];
		}
		return null;
	}, [exhibitor]);

	const logoUrl = useMemo(() => {
		if (exhibitor) {
			if (exhibitor.LOGO.includes('http')) {
				return exhibitor.LOGO;
			}
			return `./exhibitor-logos/${exhibitor.LOGO}`;
		}
		return null;
	}, [exhibitor]);

	const [relatedExhibitorsToDisplay, setRelatedExhibitorsToDisplay] = useState(3);

	const recommendedExhibitors = useMemo(() => {
		let recommendedExhibitors = [];

		if (recommendFromAPI) recommendedExhibitors = relatedExhibitorsFromAPI;
		if (!recommendFromAPI) recommendedExhibitors = relatedExhibitors;

		if (recommendedExhibitors.length > relatedExhibitorsToDisplay) {
			return recommendedExhibitors.slice(0, relatedExhibitorsToDisplay);
		}

		return recommendedExhibitors;
	}, [recommendFromAPI, relatedExhibitorsFromAPI, relatedExhibitors, relatedExhibitorsToDisplay]);

	const showMoreButton = useMemo(() => {
		if (relatedExhibitors.length > relatedExhibitorsToDisplay) {
			return true;
		}
		return false;
	}, [relatedExhibitors, relatedExhibitorsToDisplay]);

	useEffect(() => {
		if (selectedExhibitor) {
			setRelatedExhibitorsToDisplay(3);
		}
	}, [selectedExhibitor]);

	const rightContent = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const sug = params.get('sug');
		const configSUG = showRightContent;

		if (!configSUG) return false;
		if (configSUG && sug === 'false') return false;
		return true;
	}, [showRightContent]);

	const productImageUrl = useMemo(() => {
		if (product) {
			if (product['產品圖'].includes('http')) {
				return product['產品圖'];
			}
			return `./product-photos/${product['產品圖']}`;
		}
		return null;
	}, [product]);

	useEffect(() => {
		if (exhibitor && !product) {
			if (exhibitor.products.length) {
				setProduct(exhibitor.products[0]);
			}
		}
	}, [exhibitor, product, setProduct]);

	if (!exhibitor) return null;

	return (
		<div id='ExhibitorInfo' className={ExhibitorInfo ? 'active' : ''} onClick={close}>
			<div className={`center-content ${rightContent ? '' : 'narrow'}`}>
				<IconClose onClick={closePopup} />

				<div className='logo-wrap'>
					<img src={logoUrl} alt='LOGO' />
				</div>

				<div className='title'>{exhibitor['廠商名稱']}</div>

				<div className='scroller' ref={scrollerRef}>
					<div className='flex-wrap-fixed'>
						<div className='left-content'>
							<div className='flex-between'>
								<div className='controls'>
									<div className='flex-control' onClick={toggleFavouriteExhibitor}>
										<div className='icon'>{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}</div>
										<div className='text'>
											{translation('exhibitorPopup.booth')}：{selectedExhibitor}
										</div>
									</div>

									<div className='flex-control' onClick={navigateToBooth}>
										<div className='icon marker'>
											<IconMarkerSearch />
										</div>
										<div className='text'>{translation('exhibitorPopup.navigationPath')}</div>
									</div>
								</div>

								<div className='links'>
									{exhibitor['社群連結-IG'] ? (
										<a href={exhibitor['社群連結-IG']} target='_blank' rel='noreferrer noopener'>
											<IconInstagram />
										</a>
									) : null}

									{exhibitor['社群連結-FB'] ? (
										<a href={exhibitor['社群連結-FB']} target='_blank' rel='noreferrer noopener'>
											<IconFacebook />
										</a>
									) : null}

									{exhibitor['社群連結-LINE'] ? (
										<a href={exhibitor['社群連結-LINE']} target='_blank' rel='noreferrer noopener'>
											<IconLINE />
										</a>
									) : null}
								</div>
							</div>

							<div className='tags'>
								{categories.map((category, index) => (
									<div className='tag' key={`uc${index}`}>
										#{category}
									</div>
								))}
							</div>

							{exhibitor['官網連結'] ? (
								<div className='website'>
									<a href={exhibitor['官網連結']} target='_blank' rel='noreferrer noopener'>
										{exhibitor['官網連結']}
									</a>
								</div>
							) : null}

							<div className='location'>
								{city}
								{city && `, `}
								{exhibitor['國別']}
							</div>

							<div className='description' dangerouslySetInnerHTML={{ __html: textareaToHTML(exhibitor['展商基本介紹']) }} />

							<div className={`products ${useSlider ? '' : 'static'}`}>
								{useSlider ? (
									<Slider
										infinite={true}
										slidesToShow={slidesToShow}
										dots={false}
										arrows={true}
										swipeToSlide={true}
										prevArrow={<Prev />}
										nextArrow={<Next />}
										ref={sliderRef}
										responsive={[
											{
												breakpoint: 768,
												settings: {
													slidesToShow: slidesToShow,
												},
											},
											{
												breakpoint: 480,
												settings: {
													slidesToShow: 1,
												},
											},
										]}>
										{exhibitor.products.map((product, index) => {
											const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
											return (
												<div className='product-wrap' key={`exhibitor-product-${index}`} onClick={() => showProduct(product)}>
													<div className='product'>
														<div className='image-wrap' style={{ backgroundImage: `url(${imageUrl})` }}></div>
														<div className='product-name'>{product['產品名稱']}</div>
													</div>
												</div>
											);
										})}
									</Slider>
								) : (
									// 靜態顯示（不使用 slick）
									exhibitor.products.map((product, index) => {
										const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
										return (
											<div className='product-wrap' key={`exhibitor-product-static-${index}`} onClick={() => showProduct(product)}>
												<div className='product'>
													<div className='image-wrap' style={{ backgroundImage: `url(${imageUrl})` }}></div>
													<div className='product-name'>{product['產品名稱']}</div>
												</div>
											</div>
										);
									})
								)}
							</div>

							{product ? (
								<div className='product-detail'>
									<div className='product-image' style={{ backgroundImage: `url(${productImageUrl})` }}></div>
									<div className='product-name'>{product['產品名稱']}</div>
									<div className='product-details' dangerouslySetInnerHTML={{ __html: textareaToHTML(product['產品說明']) }}></div>
								</div>
							) : null}

							<div className='mobile-products'>
								{exhibitor.products.map((prod, index) => {
									const imageUrl = prod['產品圖'].includes('http') ? prod['產品圖'] : `./product-photos/${prod['產品圖']}`;

									return (
										<div className='product-detail-mobile' key={`prodetail${index}`}>
											<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
											<div className='product-name'>{prod['產品名稱']}</div>
											<div className='product-details' dangerouslySetInnerHTML={{ __html: textareaToHTML(prod['產品說明']) }}></div>
										</div>
									);
								})}
							</div>
						</div>

						{rightContent ? (
							<div className='right-content'>
								<div className='content-title'>{translation('exhibitorPopup.recommended')}</div>

								<div className='related-exhibitors'>
									{recommendedExhibitors.map((exhibitor, index) => {
										return (
											<div className='exhibitor' key={`related-exhibitor-${index}`}>
												<div
													className='exhibitor-name'
													onClick={() => {
														chooseExhibitor(exhibitor);
														recordExhibitorClicked(exhibitor['攤位號碼']);
													}}>
													{exhibitor['廠商名稱']}
												</div>
												<div className='related-products'>
													{exhibitor.products.map((product, i) => {
														if (i > 2) return null;
														const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
														return (
															<div
																className='product-wrap'
																key={`related-product-${index}${i}`}
																onClick={() => {
																	chooseProduct(exhibitor, i, product);
																	if (scrollerRef.current && typeof scrollerRef.current.scrollTo === 'function') {
																		scrollerRef.current.scrollTo(0, 0);
																	}
																}}>
																<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
																<div className='product-name'>{product['產品名稱']}</div>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>

								{showMoreButton ? (
									<div className='flex-button'>
										<div
											className='show-more'
											onClick={() => {
												setRelatedExhibitorsToDisplay(relatedExhibitorsToDisplay + 3);
											}}>
											{translation('exhibitorPopup.showMore')}
										</div>
									</div>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExhibitorInfo;
